import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBranches } from '../../api/salon';
import { fetchServices } from '../../api/services';
import {
  fetchStaffActivity,
  updateStaffBranchSchedules,
  updateStaffPermissions,
  updateStaffSpecialties,
} from '../../api/staff';
import StaffBranchScheduleEditor, { toApiSchedules, toEditorSchedules } from './StaffBranchScheduleEditor';
import StaffSalaryPanel from './StaffSalaryPanel';
import { activityIcon, describeActivity } from '../../utils/activityLabels';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const PERM_KEYS = [
  'canManageAppointments',
  'canManageOrders',
  'canViewAnalytics',
  'canManageProducts',
  'canManageInventory',
  'canManageStaff',
  'canManageSalaries',
  'canUsePos',
];

const StaffDetailDrawer = ({ staff, onClose }) => {
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const { showToast } = useToast();
  const canManageSalaries =
    role === 'SALON_MANAGER' || role === 'SUPER_ADMIN' || !!user?.canManageSalaries;
  const isSalaryOwner = role === 'SALON_MANAGER' || role === 'SUPER_ADMIN';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perms, setPerms] = useState(staff?.permissions || {});
  const [selectedServiceIds, setSelectedServiceIds] = useState(staff?.serviceIds || []);
  const [serviceSurcharges, setServiceSurcharges] = useState({});
  const [branchSchedules, setBranchSchedules] = useState([]);

  useEffect(() => {
    if (staff?.permissions) setPerms(staff.permissions);
  }, [staff]);

  useEffect(() => {
    setSelectedServiceIds(staff?.serviceIds || []);
    const map = {};
    (staff?.serviceSurcharges || staff?.services || []).forEach((row) => {
      const id = row.serviceId?.toString?.() || row._id?.toString?.();
      if (id) map[id] = row.extraAmount ? String(row.extraAmount) : '';
    });
    setServiceSurcharges(map);
  }, [staff?._id, staff?.serviceIds, staff?.serviceSurcharges, staff?.services]);

  useEffect(() => {
    setBranchSchedules(toEditorSchedules(staff?.branchSchedules || []));
  }, [staff?._id, staff?.branchSchedules]);

  const { data: servicesData } = useQuery({
    queryKey: ['services', 'staff-specialties'],
    queryFn: () => fetchServices({ limit: 200 }),
    enabled: !!staff?._id,
  });

  const { data: branchesData } = useQuery({
    queryKey: ['salon-branches'],
    queryFn: fetchBranches,
    enabled: !!staff?._id,
  });

  const { data: activityData } = useQuery({
    queryKey: ['staff-activity', staff?._id, page],
    queryFn: () => fetchStaffActivity(staff._id, { page, limit: 10 }),
    enabled: !!staff?._id,
  });

  const permMut = useMutation({
    mutationFn: (next) => updateStaffPermissions(staff._id, next),
    onSuccess: () => {
      showToast(t('toast.permissionsSaved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: () => showToast(t('toast.permissionsUpdateFailed'), 'error'),
  });

  const specialtiesMut = useMutation({
    mutationFn: (payload) => updateStaffSpecialties(staff._id, payload),
    onSuccess: () => {
      showToast(t('toast.specialtiesSaved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['salon-staff'] });
    },
    onError: () => showToast(t('toast.specialtiesUpdateFailed'), 'error'),
  });

  const branchSchedulesMut = useMutation({
    mutationFn: (schedules) => updateStaffBranchSchedules(staff._id, schedules),
    onSuccess: () => {
      showToast(t('toast.branchSchedulesSaved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['salon-staff'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.branchSchedulesUpdateFailed'), 'error'),
  });

  if (!staff) return null;

  const logs = activityData?.data || [];
  const pagination = activityData?.pagination;
  const salonServices = servicesData?.services || [];
  const initialSurchargePayload = () =>
    (staff?.serviceSurcharges || staff?.services || [])
      .filter((row) => (staff?.serviceIds || []).includes(row.serviceId || row._id))
      .map((row) => ({
        serviceId: row.serviceId || row._id,
        extraAmount: Number(row.extraAmount) || 0,
      }))
      .sort((a, b) => String(a.serviceId).localeCompare(String(b.serviceId)));

  const currentSurchargePayload = () =>
    selectedServiceIds
      .map((id) => ({
        serviceId: id,
        extraAmount: Number(serviceSurcharges[id]) || 0,
      }))
      .sort((a, b) => String(a.serviceId).localeCompare(String(b.serviceId)));

  const specialtiesDirty =
    JSON.stringify([...selectedServiceIds].sort()) !== JSON.stringify([...(staff.serviceIds || [])].sort())
    || JSON.stringify(currentSurchargePayload()) !== JSON.stringify(initialSurchargePayload());
  const branchSchedulesDirty =
    JSON.stringify(toApiSchedules(branchSchedules)) !== JSON.stringify(toApiSchedules(toEditorSchedules(staff.branchSchedules || [])));

  const togglePerm = (key) => {
    const next = { ...perms, [key]: !perms[key] };
    setPerms(next);
    permMut.mutate(next);
  };

  const toggleService = (serviceId) => {
    setSelectedServiceIds((prev) => {
      if (prev.includes(serviceId)) {
        setServiceSurcharges((s) => {
          const next = { ...s };
          delete next[serviceId];
          return next;
        });
        return prev.filter((id) => id !== serviceId);
      }
      return [...prev, serviceId];
    });
  };

  const saveSpecialties = () => {
    specialtiesMut.mutate({
      serviceIds: selectedServiceIds,
      serviceSurcharges: currentSurchargePayload(),
    });
  };

  const saveBranchSchedules = () => {
    branchSchedulesMut.mutate(toApiSchedules(branchSchedules));
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">{t('staff.detail.profileTitle')}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="flex items-center gap-4">
            {staff.profilePicture ? (
              <img src={staff.profilePicture} alt="" className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-xl font-medium">
                {staff.firstName?.[0]}{staff.lastName?.[0]}
              </div>
            )}
            <div>
              <p className="text-lg font-medium">{staff.firstName} {staff.lastName}</p>
              <p className="text-sm text-zinc-600 dark:text-zinc-500">{staff.email}</p>
              <p className="text-xs text-zinc-600 dark:text-zinc-500">{staff.phone}</p>
            </div>
          </div>

          <div className="mt-6">
            <p className="mb-1 text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('staff.detail.serviceSpecialties')}</p>
            <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-500">{t('staff.detail.serviceSpecialtiesHint')}</p>
            <p className="mb-3 text-xs text-zinc-500">{t('staff.detail.serviceSurchargeHint')}</p>
            {salonServices.length === 0 ? (
              <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('staff.detail.noServicesAvailable')}</p>
            ) : (
              <div className="space-y-2">
                {salonServices.map((service) => {
                  const id = service._id?.toString?.() || String(service._id);
                  return (
                    <label
                      key={id}
                      className="flex flex-col gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span>{service.name}</span>
                        <input
                          type="checkbox"
                          checked={selectedServiceIds.includes(id)}
                          onChange={() => toggleService(id)}
                        />
                      </div>
                      {selectedServiceIds.includes(id) && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{t('staff.detail.extraCharge')}</span>
                          <input
                            type="number"
                            min={0}
                            value={serviceSurcharges[id] ?? ''}
                            onChange={(e) =>
                              setServiceSurcharges((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                            placeholder="0"
                            className="w-28 rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1 text-xs"
                          />
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            {salonServices.length > 0 && (
              <button
                type="button"
                disabled={!specialtiesDirty || specialtiesMut.isPending}
                onClick={saveSpecialties}
                className="mt-3 w-full rounded-lg bg-glow-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {specialtiesMut.isPending ? t('common.saving') : t('staff.detail.saveSpecialties')}
              </button>
            )}
          </div>

          <div className="mt-6">
            <StaffBranchScheduleEditor
              branches={branchesData?.branches || []}
              value={branchSchedules}
              onChange={setBranchSchedules}
            />
            <button
              type="button"
              disabled={!branchSchedulesDirty || branchSchedulesMut.isPending}
              onClick={saveBranchSchedules}
              className="mt-3 w-full rounded-lg bg-glow-600 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {branchSchedulesMut.isPending ? t('common.saving') : t('staff.detail.saveBranchSchedules')}
            </button>
          </div>

          {canManageSalaries && (
            <div className="mt-6">
              <StaffSalaryPanel staffId={staff._id} isOwner={isSalaryOwner} />
            </div>
          )}

          <div className="mt-6">
            <p className="mb-2 text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('staff.detail.permissions')}</p>
            <div className="space-y-2">
              {PERM_KEYS.map((key) => (
                <label key={key} className="flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-sm">
                  <span>{t(`staff.permissions.${key}`)}</span>
                  <input type="checkbox" checked={!!perms[key]} onChange={() => togglePerm(key)} />
                </label>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <p className="mb-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('staff.detail.activityTimeline')}</p>
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log._id} className="flex gap-3">
                  <span className="text-lg">{activityIcon(log.action)}</span>
                  <div>
                    <p className="text-sm">{describeActivity(log)}</p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">
                      {new Date(log.createdAt).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              ))}
              {logs.length === 0 && <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('staff.detail.noActivity')}</p>}
            </div>
            {pagination && pagination.pages > 1 && (
              <div className="mt-4 flex justify-between">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm text-glow-400 disabled:opacity-40">
                  {t('common.prev')}
                </button>
                <span className="text-xs text-zinc-600 dark:text-zinc-500">
                  {t('common.pageOf', { page, pages: pagination.pages })}
                </span>
                <button type="button" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)} className="text-sm text-glow-400 disabled:opacity-40">
                  {t('common.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default StaffDetailDrawer;
